/// <reference types="google.maps" />

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  finalize,
  map,
  takeUntil,
  tap
} from 'rxjs/operators';
import { parseISO } from 'date-fns';

import { COMMON } from '@sondermind/constants';
import {
  AUStateNameMap,
  SupportedCountries,
  SupportedCountryMap,
  USStateNameMap
} from '@sondermind/utilities/states-list';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { UserDataService } from '@sondermind/data-access/client-facing/user-data';
import { ReferenceDataHttpService, ReferenceDataTypes } from '@sondermind/data-access/reference-data';
import { ServiceError } from '@sondermind/utilities/models-http';
import { SharedMasks, TextMasks } from '@sondermind/utilities/text-masks';

import { toTimeZoneDateTime } from '@sondermind/utilities/time-and-date';
import { ClientUserProfile } from '@sondermind/utilities/models-user-client';

import {
  IrisButtonContainerAlignments,
  IrisButtonTypes
} from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { BirthdayValidator } from '@sondermind/utilities/forms';
import { scrollToFirstInvalidControl } from '@sondermind/iris/form-helpers';
import { ProviderStore } from '@sondermind/data-access/client-portal-referrals';
import { NotificationToastService, ToastPosition, ToastType } from '@sondermind/features/notification-toast';
import { GoogleMapsGeocodeService } from '@sondermind/google-maps';
import { Logger } from '@sondermind/datadog-logging';
import { setAddressObject } from '@sondermind/core/utilities/autocomplete-address-selector';

/**
 * Which account-info sub-step is shown in the direct-booking flow. Shared with the parent
 * (setup-basic-info) which reads it to decide when to show the Back button.
 */
export enum ProfileSubStep {
  Personal = 'personal',
  Contact = 'contact'
}

interface IIrisDropdownOption {
  name: string;
  value: string | number;
}

interface ICommunicationPreferencesFormControls {
  commPrefsPhone: FormControl<boolean | null>;
  commPrefsSms: FormControl<boolean | null>;
}

export interface IProfileEditFormControls {
  id: FormControl<string | number | null>;
  userId: FormControl<string | number | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  preferredFirstName: FormControl<string | null>;
  birthDate: FormControl<string | null>;
  phoneNumber: FormControl<string | null>;
  mailingCountry: FormControl<string | null>;
  mailingAddressLine1: FormControl<string | null>;
  mailingAddressLine2: FormControl<string | null>;
  mailingCity: FormControl<string | null>;
  mailingState: FormControl<string | null>;
  mailingPostalCode: FormControl<string | null>;
  timeZone: FormControl<string | null>;
  genderId: FormControl<string | number | null>;
  verification: FormControl<boolean | null>;
  commPrefs: FormGroup<ICommunicationPreferencesFormControls>;
}
@Component({
  selector: 'client-profile-edit-form',
  templateUrl: './profile-edit-form.component.html',
  styleUrls: ['./profile-edit-form.component.scss'],
  standalone: false
})
export class ProfileEditFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() useInSessionRequest: boolean = false;
  @Input() accountComplete: boolean = false;
  @Input() isProposeATime: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() fromDirectBooking: boolean = false;
  @Output() basicInfoFirstSubmit: EventEmitter<void> = new EventEmitter<void>();

  // The field just below the address input; scrolled into view after a Google autocomplete selection
  // so the user sees the fields it just filled and the path forward instead of staying on the address.
  @ViewChild('addressFollowingField', { read: ElementRef }) addressFollowingField?: ElementRef<HTMLElement>;

  private destroyed$ = new Subject<void>();

  irisButtonContainerAlignments = IrisButtonContainerAlignments;
  irisButtonTypes = IrisButtonTypes;
  irisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;

  constructor(
    private currentUserService: CurrentUser,
    private referenceDataHttpService: ReferenceDataHttpService,
    private formBuilder: FormBuilder,
    private notificationToastService: NotificationToastService,
    private userDataService: UserDataService,
    private geocodeService: GoogleMapsGeocodeService,
    private logger: Logger,
    private elementRef: ElementRef<HTMLElement>,
    // NOTE: USED FOR PROJECT JUNO
    private providerStore: ProviderStore,
  ) { }

  masks: TextMasks = SharedMasks;
  zipcodeMask = this.masks['zipcodeMask'];
  phoneNumberMask = this.masks['phoneMask'];

  readonly stateOptions = signal<Array<{ name: string; value: string; }>>(USStateNameMap);
  supportedCountryOptions = SupportedCountryMap;
  profileForm: FormGroup<Partial<IProfileEditFormControls>> = new FormGroup({});
  dateToday: Date = new Date();
  maxDate: string = `${this.dateToday.getFullYear()}-${String(this.dateToday.getMonth() + 1).padStart(2, '0')
    }-${String(this.dateToday.getDate()).padStart(2, '0')
    }`;
  error: string | null = null;
  submitting: boolean = false;
  accountStatus: string;
  supportPhoneLink = COMMON.SUPPORT_PHONE_NO_MATCH_LINK;
  supportPhoneDisplay = COMMON.SUPPORT_PHONE_NO_MATCH_DISPLAY;
  supportEmail = COMMON.SUPPORT_EMAIL;
  // used to determine when all fields are needed, such as if data did not carry over from match flow
  useFullForm: boolean = false;
  mobileBday: string;

  profile$: Observable<ClientUserProfile | null> = of(null);
  gender$: Observable<string>;
  formReady$ = new BehaviorSubject<boolean>(false);

  genderOptions: IIrisDropdownOption[] = [];

  genderOptions$: Observable<IIrisDropdownOption[]>;

  // JUNO: We add an additional field for clients to select their country
  // first if they are saas-invited (SAAS clients can be outside of the US)
  readonly saasInvited = signal<boolean>(false);
  saasSupportEmail = COMMON.SAAS_SUPPORT_EMAIL;
  readonly addressFieldsDisabled = signal<boolean>(false);

  skipAcknowledgeInfo: boolean = false;

  // Direct-booking only: chunk the long account-info form into two shorter sub-steps so the user
  // isn't faced with a single endless scroll. Personal details first, then contact & location.
  // Only engaged when the full form is shown (i.e. the match flow didn't already collect name/gender);
  // otherwise there is nothing to put in the personal sub-step and it stays a single view.
  subStep: ProfileSubStep = ProfileSubStep.Personal;
  showSubSteps: boolean = false;
  showPersonalGroup: boolean = true;
  showContactGroup: boolean = true;
  accountInfoHeading: string = 'Account info';
  private subStepsConfigured: boolean = false;

  private readonly personalFieldNames = ['firstName', 'lastName', 'preferredFirstName', 'birthDate', 'genderId'];

  ngOnInit(): void {
    this.providerStore.isClientSaasInvited$().pipe(takeUntil(this.destroyed$)).subscribe(
      (saasInvited) => {
        this.saasInvited.set(saasInvited);

        this.profileDataInit();
        this.profileInit();

        this.accountStatus = this.accountComplete ? 'updating' : 'completing';
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isMobile'] && changes['isMobile'].currentValue !== changes['isMobile'].previousValue) {
      this.setBday();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Handle a Google Places autocomplete selection: populate the address fields from the
   * selected place's address components, then resolve the time zone for the selected
   * location so it is saved alongside the address. Restricted to street addresses
   * (`types: ['address']`).
   */
  onAddressSelected(place: google.maps.places.PlaceResult): void {
    if (!place?.address_components) {
      return;
    }

    setAddressObject(
      place.address_components,
      this.profileForm as FormGroup,
      {
        city: 'mailingCity',
        state: 'mailingState',
        addressLine1: 'mailingAddressLine1',
        addressLine2: 'mailingAddressLine2',
        postalCode: 'mailingPostalCode'
      }
    );

    this.updateTimeZoneFromPlace(place);
    this.scrollPastAddress();
  }

  /**
   * After the address autocomplete fills the city/state/zip fields, scroll the modal's scroll region
   * all the way to the bottom so every remaining field (phone, timezone, comm prefs) and the way
   * forward are visible without the user having to scroll further. No-op if the anchor isn't rendered.
   */
  private scrollPastAddress(): void {
    setTimeout(() => {
      const anchor = this.addressFollowingField?.nativeElement;
      if (!anchor) {
        return;
      }
      const prefersReducedMotion = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

      const scrollContainer = this.findScrollableAncestor(anchor);
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior });
        return;
      }
      anchor.scrollIntoView({ behavior, block: 'center' });
    }, 0);
  }

  /** Walk up from `el` to the nearest vertically-scrollable ancestor (the modal's scroll region). */
  private findScrollableAncestor(el: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = el.parentElement;
    while (current) {
      const { overflowY } = getComputedStyle(current);
      const isScrollable = (overflowY === 'auto' || overflowY === 'scroll')
        && current.scrollHeight > current.clientHeight;
      if (isScrollable) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  /**
   * Resolve the IANA time zone for the selected place's coordinates and patch it into the
   * (otherwise read-only) `timeZone` control so it is submitted with the profile. On any
   * failure the existing zone is left untouched.
   */
  private updateTimeZoneFromPlace(place: google.maps.places.PlaceResult): void {
    const location = place.geometry?.location;
    if (!location) {
      return;
    }

    const lat = location.lat();
    const lng = location.lng();

    this.geocodeService.getTimeZoneId$(lat, lng).pipe(
      takeUntil(this.destroyed$)
    ).subscribe((timeZoneId) => {
      if (timeZoneId) {
        this.profileForm.controls.timeZone?.setValue(timeZoneId);
      } else {
        this.logger.warn('ProfileEditForm: could not resolve time zone for selected address');
      }
    });
  }

  /**
   * Decide whether to split the account-info form into two sub-steps. Only in the direct-booking
   * modal, only while completing an incomplete account, and only when the full form is shown — if the
   * match flow already collected name/gender (`useFullForm` false) there is nothing for the personal
   * sub-step, so we keep a single view.
   */
  configureSubSteps(): void {
    this.showSubSteps = this.fromDirectBooking && this.useFullForm && !this.accountComplete;
    // Only reset to the first sub-step on the initial configuration. profileInit subscribes to a
    // re-emitting source (user$); without this guard a later emission would bounce the user back to
    // "personal" and discard their in-progress contact-step edits.
    if (!this.subStepsConfigured) {
      this.subStep = ProfileSubStep.Personal;
      this.subStepsConfigured = true;
    }
    this.applySubStepVisibility();
  }

  private applySubStepVisibility(): void {
    if (!this.showSubSteps) {
      this.showPersonalGroup = true;
      this.showContactGroup = true;
      this.accountInfoHeading = 'Account info';
      return;
    }

    const onPersonal = this.subStep === ProfileSubStep.Personal;
    this.showPersonalGroup = onPersonal;
    this.showContactGroup = !onPersonal;
    this.accountInfoHeading = onPersonal ? 'Personal details' : 'Contact & location';
  }

  /**
   * Whether the currently visible sub-step's controls are all valid. When sub-steps are off this is
   * just the whole form's validity.
   */
  private currentSubStepValid(): boolean {
    if (!this.showSubSteps || this.subStep !== ProfileSubStep.Personal) {
      return this.profileForm.valid;
    }
    return this.personalFieldNames.every((name) => {
      const control = this.profileForm.get(name);
      return !control || control.valid;
    });
  }

  /**
   * Handle the parent's "Next": on the first sub-step, advance to the second (if that sub-step's
   * fields are valid); otherwise submit. Returns true if the caller should treat this as a submit.
   */
  goToNextSubStep(): boolean {
    if (this.showSubSteps && this.subStep === ProfileSubStep.Personal) {
      if (!this.revealValidationErrors()) {
        return false;
      }
      this.subStep = ProfileSubStep.Contact;
      this.applySubStepVisibility();
      return false;
    }
    return true;
  }

  goToPreviousSubStep(): void {
    if (this.showSubSteps && this.subStep === ProfileSubStep.Contact) {
      this.subStep = ProfileSubStep.Personal;
      this.applySubStepVisibility();
    }
  }

  /**
   * Reveal Iris inline validation errors on the visible sub-step's controls and scroll to the first
   * invalid one. Iris only renders errors on `invalid && touched`, so this is how an incomplete form
   * communicates what is required after the user tries to advance. Returns whether the visible fields
   * are valid.
   */
  revealValidationErrors(): boolean {
    if (this.currentSubStepValid()) {
      return true;
    }
    this.profileForm.markAllAsTouched();
    scrollToFirstInvalidControl(this.elementRef.nativeElement);
    return false;
  }

  /**
   * Submit the user's profile form. Reveals validation errors and bails if invalid; on success,
   * refreshes the profile and resets the form (account-complete) or notifies the parent.
   */
  submitForm(): void {
    if (!this.revealValidationErrors()) {
      return;
    }

    this.startSubmitting();
    const birthDateString = this.profileForm.value.birthDate?.toString() || '';
    const bday = this.toIsoDateString(birthDateString);
    const commPrefs = this.getCommPrefsArray(this.profileForm.value.commPrefs || {});

    // Use getRawValue() so disabled controls (e.g. the read-only, address-derived timeZone) are
    // included in the payload; profileForm.value would omit them.
    const formValue = this.profileForm.getRawValue();
    const profileData: Partial<ClientUserProfile> = {
      ...formValue,
      id: typeof formValue.id === 'string' ? parseInt(formValue.id, 10) : formValue.id,
      userId: typeof formValue.userId === 'string' ? parseInt(formValue.userId, 10) : formValue.userId,
      genderId: typeof formValue.genderId === 'string' ? parseInt(formValue.genderId, 10) : formValue.genderId,
      birthDate: bday,
      communicationPreferences: commPrefs
    };

    this.userDataService.updateProfile(profileData, { verify: true }).pipe(
      finalize(() => this.stopSubmitting())
    ).subscribe({
      next: (userProfile: ClientUserProfile) => {
        this.error = null;

        if (this.accountComplete) {
          this.notificationToastService.showNotificationToast({
            message: 'Profile saved',
            autoClose: true,
            type: ToastType.SUCCESS,
            position: ToastPosition.BOTTOM_CENTER
          });
          this.currentUserService.refreshProfile(userProfile);
          this.profileForm.reset(this.profileForm.value);
          this.addressFieldsDisabled.set(false);
          this.toggleAddressFields(false);
        } else {
          // if used by incomplete account, tell parent form submit is complete
          this.basicInfoFirstSubmit.emit();
        }
      },
      error: (err) => {
        this.setErrorMessage(err);
      }
    });
  }

  parseDateString(dateString: string, timeZone: string): Date {
    return toTimeZoneDateTime(parseISO(this.toIsoDateString(dateString)), timeZone).toJSDate();
  }

  getCommPrefsArray(commPrefs: Partial<{
    commPrefsPhone: boolean | null;
    commPrefsSms: boolean | null;
  }>): string[] {
    const prefs: CommunicationPreference[] = [CommunicationPreference.NCC];
    if (commPrefs.commPrefsPhone) prefs.push(CommunicationPreference.PHONE);
    if (commPrefs.commPrefsSms) prefs.push(CommunicationPreference.SMS);
    return prefs;
  }

  /**
    * In mobile, we swap the datepicker to one that returns values in mm/dd/yyyy format.
    * Rather than assuming the datestring is in mm/dd/yyyy format when isMobile = true,
    * check the format via regex to account for when:
    *   - the value is unchanged (i.e., user.profile.birth_date was already set),
    *   - the viewport changes from desktop to mobile and vice versa.
    */
  toIsoDateString(dateString: string): string {
    const isMmDdYyyy = /^\d{2}\/\d{2}\/\d{4}$/.test(dateString);
    if (isMmDdYyyy) {
      const [month, day, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }

    return dateString;
  }

  /**
   * Initialize all observables related to the user's profile edit form.
   */
  profileDataInit(): void {
    const filterAndSort = {
      filter: [{ key: 'hidden', value: 'false' }],
      sort: [{ key: 'order_number', asc: true }]
    };

    this.gender$ = this.profile$.pipe(map((p) => (p ? p.gender || 'na' : null)));
    this.genderOptions$ = this.referenceDataHttpService.list(
      ReferenceDataTypes.genders,
      filterAndSort
    ).pipe(
      map((response) => response.data),
      map((genders) => genders.map((gender) => ({
        name: gender.title,
        value: gender.id
      })))
    );
  }
  /**
   * Initialize the user profile observable, then build the edit form.
   */
  profileInit(): void {
    this.useFullForm = this.accountComplete;

    // Now these observables are guaranteed to be initialized
    this.profile$ = combineLatest({
      user: this.currentUserService.user$,
      genderOptions: this.genderOptions$,
    }).pipe(
      tap(({ genderOptions, user }) => {
        this.genderOptions = genderOptions;
        this.skipAcknowledgeInfo = user?.skipAcknowledgeInfo ?? false;
      }),
      map(({ user }) => (user ? user.profile : null))
    );

    this.profile$.subscribe((profile) => {
      if (profile) {
        if (!this.accountComplete) {
          this.checkEmptyRequiredFields(profile);
        }
        this.profileForm = this.buildForm(profile);
        this.setBday();
        this.configureSubSteps();
        this.formReady$.next(true);

        this.subscribeToCountryFieldUpdates();
      }
    });
  }

  /**
   * Build user data form, remove redundant match flow fields if account is incomplete.
   */
  private buildForm(profile: ClientUserProfile): FormGroup<Partial<IProfileEditFormControls>> {
    const formGroup = this.formBuilder.group<Partial<IProfileEditFormControls>>({
      id: new FormControl<string | number | null>(profile.id, {
        validators: Validators.required,
        nonNullable: false
      }),
      userId: new FormControl<string | number | null>(this.currentUserService.user?.id || null, {
        nonNullable: false
      }),
      firstName: new FormControl<string | null>(profile.firstName, {
        validators: Validators.required,
        nonNullable: false
      }),
      lastName: new FormControl<string | null>(profile.lastName, {
        validators: Validators.required,
        nonNullable: false
      }),
      preferredFirstName: new FormControl<string | null>(profile.preferredFirstName, {
        nonNullable: false
      }),
      birthDate: new FormControl<string | null>(profile.birthDate, {
        validators: [Validators.required, BirthdayValidator.validate],
        nonNullable: false
      }),
      phoneNumber: new FormControl<string | null>(profile.phoneNumber, {
        validators: Validators.required,
        nonNullable: false
      }),
      mailingCountry: new FormControl<string | null>(profile.mailingAddress.country, {
        nonNullable: false
      }),
      mailingAddressLine1: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.addressLine1,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false,
        }
      ),
      mailingAddressLine2: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.addressLine2,
          disabled: this.addressFieldsDisabled()
        },
        {
          nonNullable: false
        }
      ),
      mailingCity: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.city,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false
        }
      ),
      mailingState: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.state,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false
        }
      ),
      mailingPostalCode: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.postalCode,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false
        }
      ),
      timeZone: new FormControl<string | null>(
        {
          value: profile.timeZone,
          disabled: true,
        },
        {
          validators: Validators.required,
          nonNullable: false,
        }
      ),
      genderId: new FormControl<string | number | null>(profile.genderId, {
        validators: Validators.required,
        nonNullable: false
      }),
      verification: new FormControl<boolean | null>(profile.verification, {
        validators: this.skipAcknowledgeInfo ? [] : [Validators.requiredTrue],
        nonNullable: false
      }),
      commPrefs: new FormGroup<ICommunicationPreferencesFormControls>({
        commPrefsPhone: new FormControl<boolean | null>(
          profile.communicationPreferences?.includes(CommunicationPreference.PHONE),
          { nonNullable: false }
        ),
        commPrefsSms: new FormControl<boolean | null>(
          profile.communicationPreferences?.includes(CommunicationPreference.SMS),
          { nonNullable: false }
        )
      }),
    });

    if (!this.useFullForm) {
      // remove redundant fields that were asked in match flow
      formGroup.removeControl('firstName');
      formGroup.removeControl('lastName');
      formGroup.removeControl('preferredFirstName');
      formGroup.removeControl('timeZone');
      formGroup.removeControl('genderId');
    }

    if (this.accountComplete) {
      formGroup.removeControl('verification');
    }

    // For SAAS invited clients, the country field is required
    // And we enable/disable filling out the rest of the address fields based on whether
    // the country field is filled
    if (this.saasInvited()) {
      formGroup.controls.mailingCountry?.addValidators(Validators.required);
      if (formGroup.controls.mailingCountry.value) {
        this.addressFieldsDisabled.set(false);
        this.setCountrySpecificOptions(formGroup.controls.mailingCountry.value);
      }
    }

    return formGroup;
  }

  private subscribeToCountryFieldUpdates(): void {
    if (!this.saasInvited()) {
      return;
    }

    this.profileForm?.controls.mailingCountry.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      const country = value;
      if (!country) {
        this.addressFieldsDisabled.set(true);
        this.toggleAddressFields(true);
      } else {
        this.addressFieldsDisabled.set(false);
        this.toggleAddressFields(false);

        this.setCountrySpecificOptions(country);
      }
    });
  }

  private setCountrySpecificOptions(country: string): void {
    if (country === SupportedCountries.AUSTRALIA) {
      this.phoneNumberMask = this.masks['ausPhoneMask'];
      this.zipcodeMask = this.masks['ausZipcodeMask'];
      this.stateOptions.set(AUStateNameMap);
    } else {
      this.phoneNumberMask = this.masks['phoneMask'];
      this.zipcodeMask = this.masks['zipcodeMask'];
      this.stateOptions.set(USStateNameMap);
    }
  }

  /**
  * Wrapper for better unit tests.
  */
  startSubmitting(): void {
    this.submitting = true;
  }

  /**
  * Wrapper for better unit tests.
  */
  stopSubmitting(): void {
    this.submitting = false;
  }

  /**
   * set error to 'base' string if present,
   * deconstruct ServiceError and set to string if no 'base' is present
   * or set generic error in the event that no error returns from the back end
   */
  setErrorMessage(err: ServiceError): void {
    // TODO: (PE-9368) part of investigation to make sure correct error format is being used
    if (err?.base && Array.isArray(err.base)) {
      this.error = err.base.join(', ');
    } else if (err && Object.keys(err).length > 0) {
      const errorKey = Object.keys(err)[0];
      const errorValue = err[errorKey as keyof ServiceError];
      if (Array.isArray(errorValue)) {
        this.error = errorValue.join(', ');
      }
    } else if (this.saasInvited()) {
      // SAAS clients only have access to a support email
      this.error = `Something's not working—please contact us at \
        <a href="mailto:${this.saasSupportEmail}">${this.saasSupportEmail}<a> \
        to continue ${this.accountStatus} your account`;
    } else {
      this.error = `Something's not working—please contact us at \
        <a href='${this.supportPhoneLink}'>${this.supportPhoneDisplay}</a> or \
        <a href="mailto:${this.supportEmail}">${this.supportEmail}<a> \
        to continue ${this.accountStatus} your account`;
    }
  }

  /**
   * if any of these required fields has not been filled prior to arrival
   * on this page, the full form should show. (ex. Provider-invited)
   * otherwise, remove fields redundant from match flow
   * @param profile
   */
  checkEmptyRequiredFields(profile: ClientUserProfile): void {
    const requiredFields = [profile.firstName, profile.lastName, profile.gender, profile.birthDate];
    this.useFullForm = requiredFields.some((field) => field === null || field === undefined || field === '');
  }

  /**
   * Datepicker has issues on tablet or smaller depending on the device
   * so we want to show the user's bday (if it's already set) in the correct format
   * Format for mobile and desktop display.
   */
  private setBday(): void {
    const birthDateControl = this.profileForm?.controls.birthDate;
    const birthDateValue = birthDateControl?.value;

    if (birthDateValue) {
      if (this.isMobile) {
        const [year, month, day] = birthDateValue.split('-');
        this.mobileBday = `${month}/${day}/${year}`;

        birthDateControl?.setValue(this.mobileBday, { emitEvent: false });
      } else if (this.mobileBday) {
        const [month, day, year] = this.mobileBday.split('/');
        const desktopDate = `${year}-${month}-${day}`;

        birthDateControl?.setValue(desktopDate, { emitEvent: false });
      }
    }
  }

  private toggleAddressFields(shouldDisable: boolean): void {
    if (shouldDisable && this.profileForm) {
      this.profileForm.controls?.mailingAddressLine1?.disable();
      this.profileForm.controls?.mailingAddressLine2?.disable();
      this.profileForm.controls?.mailingCity?.disable();
      this.profileForm.controls?.mailingState?.disable();
      this.profileForm.controls?.mailingPostalCode?.disable();
    } else {
      this.profileForm.controls?.mailingAddressLine1?.enable();
      this.profileForm.controls?.mailingAddressLine2?.enable();
      this.profileForm.controls?.mailingCity?.enable();
      this.profileForm.controls?.mailingState?.enable();
      this.profileForm.controls?.mailingPostalCode?.enable();
    }
  }
}
